import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateWorkspace } from '../../hooks/useWorkspaces'
import { Button } from '../common/Button'
import { Input } from '../common/Input'
import { Modal } from '../common/Modal'
import './CreateWorkspaceModal.css'

export interface CreateWorkspaceModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

/**
 * CreateWorkspaceModal Component
 * Modal for creating a new workspace
 * Implements Requirement 2.2: Workspace creation assigns OWNER role
 */
export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<{ name?: string; description?: string }>(
    {}
  )

  const createWorkspace = useCreateWorkspace()

  const validateForm = (): boolean => {
    const newErrors: { name?: string; description?: string } = {}

    if (!name.trim()) {
      newErrors.name = t('validation.workspaceNameRequired')
    } else if (name.length > 100) {
      newErrors.name = t('validation.workspaceNameTooLong')
    }

    if (description && description.length > 500) {
      newErrors.description = t('validation.descriptionTooLong')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await createWorkspace.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      })

      // Reset form
      setName('')
      setDescription('')
      setErrors({})

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      // Error is handled by the mutation hook
    }
  }

  const handleClose = () => {
    if (!createWorkspace.isPending) {
      setName('')
      setDescription('')
      setErrors({})
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.create')}
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={createWorkspace.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={createWorkspace.isPending}
          >
            {t('workspace.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="create-workspace-form">
        <Input
          label={t('workspace.name')}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          placeholder={t('workspace.enterName')}
          autoFocus
          required
        />

        <Input
          label={t('workspace.descriptionOptional')}
          type="textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={errors.description}
          placeholder={t('workspace.enterDescription')}
          rows={4}
        />
      </form>
    </Modal>
  )
}
